"use client";

import {
  m as motion,
  useInView,
  useReducedMotion,
  useScroll,
  useTransform,
  type Variants,
} from "framer-motion";
import { useRef } from "react";
import Link from "next/link";
import { Map, Hammer, TrendingUp, ArrowRight } from "lucide-react";
import { useLocale } from "next-intl";
import { localizedRoutes } from "@/lib/routes/routes";

// Cadenza ~0.22s tra i token del titolo; durata ~0.5s per token.
const TOKEN_CADENCE = 0.22;
const TITLE_TOKENS = 5; // Mappa, freccia, Costruisci, freccia, Evolvi
// Istante in cui termina (circa) la sequenza del titolo: i token dopo il
// primo accumulano TOKEN_CADENCE ciascuno + la durata dell'ultimo.
const TITLE_SEQUENCE_END = (TITLE_TOKENS - 1) * TOKEN_CADENCE + 0.5; // ~1.38s
// Le card e il sottotitolo partono al termine della sequenza del titolo.
const AFTER_TITLE_BASE = 1.1;

export function MethodSection() {
  const locale = useLocale();
  const isItalian = locale.startsWith("it");
  const lang = locale.startsWith("en") ? "en" : "it";
  const aboutHref = localizedRoutes[lang].about;
  const methodHref = localizedRoutes[lang].method;
  const reduceMotion = useReducedMotion();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Variante container del titolo: rivela i token in sequenza (stagger).
  const titleContainerVariants: Variants = {
    hidden: {},
    visible: {
      transition: reduceMotion
        ? {}
        : { staggerChildren: TOKEN_CADENCE },
    },
  };

  // Variante per ogni token del titolo: dissolvenza + leggero rise.
  const tokenVariants: Variants = reduceMotion
    ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
    : {
        hidden: { opacity: 0, y: 8 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
        },
      };

  const titleAnimateState = isInView || reduceMotion ? "visible" : "hidden";

  const steps = [
    {
      icon: Map,
      step: "01",
      title: isItalian ? "Mappa" : "Map",
      description: isItalian
        ? "Partiamo da cosa ti serve davvero: capisco il contesto e fissiamo obiettivi chiari."
        : "We start from what you actually need: I understand the context and set clear goals.",
      outputs: isItalian
        ? ["Sappiamo dove andare", "Priorit\u00E0 decise insieme", "Tempi realistici"]
        : ["We know where we're going", "Priorities decided together", "Realistic timeline"],
    },
    {
      icon: Hammer,
      step: "02",
      title: isItalian ? "Costruisci" : "Build",
      description: isItalian
        ? "Lo costruisco un pezzo alla volta, sempre sotto i tuoi occhi. Niente sparizioni per settimane."
        : "I build it one piece at a time, always in front of you. No disappearing for weeks.",
      outputs: isItalian
        ? ["Aggiornamenti continui", "Controllo tutto prima di consegnarti", "Pronto all'uso, e il codice \u00E8 tuo"]
        : ["Continuous updates", "I check everything before handing over", "Ready to use, and the code is yours"],
    },
    {
      icon: TrendingUp,
      step: "03",
      title: isItalian ? "Evolvi" : "Evolve",
      description: isItalian
        ? "Il lancio \u00E8 l'inizio, non la fine. Restiamo in contatto e miglioriamo nel tempo."
        : "Launch is the beginning, not the end. We stay in touch and improve over time.",
      outputs: isItalian
        ? ["Vediamo cosa funziona", "Miglioramenti nel tempo", "Supporto quando ti serve"]
        : ["We see what works", "Improvements over time", "Support when you need it"],
    },
  ];

  const { scrollYProgress } = useScroll();

  const patternY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

  return (
      <section
        id="method"
        ref={ref}
        className="section-padding relative overflow-hidden"
      >
      <motion.div
        className="absolute inset-0 pattern-grid opacity-50"
        style={{ y: patternY }}
      />

      <div className="container-wide relative">
        <div className="text-left mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="font-mono text-label text-primary mb-3 block"
          >
            {isItalian ? "Il mio metodo" : "My method"}
          </motion.span>
          <h2 className="font-display text-[clamp(2.05rem,9vw,2.8rem)] md:text-h2 font-bold mb-4 leading-tight md:leading-none">
            {isItalian ? (
              <>
                <motion.span
                  className="flex flex-col gap-3 md:hidden text-left leading-[1.05]"
                  variants={titleContainerVariants}
                  initial="hidden"
                  animate={titleAnimateState}
                >
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow self-start"
                    style={{ animationDelay: "0s" }}
                  >
                    <span className="text-shine">M</span>appa
                  </motion.span>
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow self-center"
                    style={{ animationDelay: "0.4s" }}
                  >
                    <span className="text-shine">C</span>ostruisci
                  </motion.span>
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow self-end"
                    style={{ animationDelay: "0.8s" }}
                  >
                    <span className="text-shine">E</span>volvi
                  </motion.span>
                </motion.span>
                <motion.span
                  className="hidden md:inline"
                  variants={titleContainerVariants}
                  initial="hidden"
                  animate={titleAnimateState}
                >
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block"
                    style={{ animationDelay: "0s" }}
                  >
                    <span className="text-shine">M</span>appa
                  </motion.span>{" "}
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block text-primary"
                    style={{ animationDelay: "0.26s" }}
                  >
                    &rarr;
                  </motion.span>{" "}
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block"
                    style={{ animationDelay: "0.52s" }}
                  >
                    <span className="text-shine">C</span>ostruisci
                  </motion.span>{" "}
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block text-primary"
                    style={{ animationDelay: "0.78s" }}
                  >
                    &rarr;
                  </motion.span>{" "}
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block"
                    style={{ animationDelay: "1.04s" }}
                  >
                    <span className="text-shine">E</span>volvi
                  </motion.span>
                </motion.span>
              </>
            ) : (
              <>
                <motion.span
                  className="flex flex-col gap-3 md:hidden text-left leading-[1.05]"
                  variants={titleContainerVariants}
                  initial="hidden"
                  animate={titleAnimateState}
                >
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow self-start"
                    style={{ animationDelay: "0s" }}
                  >
                    <span className="text-shine">M</span>ap
                  </motion.span>
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow self-center"
                    style={{ animationDelay: "0.4s" }}
                  >
                    <span className="text-shine">B</span>uild
                  </motion.span>
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow self-end"
                    style={{ animationDelay: "0.8s" }}
                  >
                    <span className="text-shine">E</span>volve
                  </motion.span>
                </motion.span>
                <motion.span
                  className="hidden md:inline"
                  variants={titleContainerVariants}
                  initial="hidden"
                  animate={titleAnimateState}
                >
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block"
                    style={{ animationDelay: "0s" }}
                  >
                    <span className="text-shine">M</span>ap
                  </motion.span>{" "}
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block text-primary"
                    style={{ animationDelay: "0.26s" }}
                  >
                    &rarr;
                  </motion.span>{" "}
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block"
                    style={{ animationDelay: "0.52s" }}
                  >
                    <span className="text-shine">B</span>uild
                  </motion.span>{" "}
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block text-primary"
                    style={{ animationDelay: "0.78s" }}
                  >
                    &rarr;
                  </motion.span>{" "}
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block"
                    style={{ animationDelay: "1.04s" }}
                  >
                    <span className="text-shine">E</span>volve
                  </motion.span>
                </motion.span>
              </>
            )}
          </h2>
          <motion.p
            className="text-body-lg text-muted-foreground max-w-5xl xl:whitespace-nowrap"
            initial={{ opacity: 0, y: 8 }}
            animate={
              isInView || reduceMotion ? { opacity: 1, y: 0 } : {}
            }
            transition={
              reduceMotion
                ? { duration: 0 }
                : {
                    duration: 0.5,
                    delay: TITLE_SEQUENCE_END,
                    ease: [0.16, 1, 0.3, 1],
                  }
            }
          >
            {isItalian ? (
              <>
                Un <span className="text-foreground">processo semplice</span>,
                soprattutto <span className="text-foreground">a distanza</span> e{" "}
                <span className="text-foreground">senza riunioni inutili</span>,
                con un solo responsabile:{" "}
                <Link
                  href={aboutHref}
                  className="font-semibold text-primary underline-offset-4 transition-colors hover:underline"
                >
                  io
                </Link>
                .
              </>
            ) : (
              <>
                A <span className="text-foreground">simple process</span>, mostly{" "}
                <span className="text-foreground">remote</span> and{" "}
                <span className="text-foreground">without pointless meetings</span>
                , with a single owner:{" "}
                <Link
                  href={aboutHref}
                  className="font-semibold text-primary underline-offset-4 transition-colors hover:underline"
                >
                  me
                </Link>
                .
              </>
            )}
          </motion.p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView || reduceMotion ? { opacity: 1, y: 0 } : {}}
              transition={
                reduceMotion
                  ? { duration: 0 }
                  : {
                      duration: 0.5,
                      delay: AFTER_TITLE_BASE + 0.15 * index,
                      ease: [0.16, 1, 0.3, 1],
                    }
              }
              className="relative"
            >
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-12 left-full w-full">
                  <div className="flex items-center">
                    <div className="flex-1 h-px bg-gradient-to-r from-primary/50 to-transparent" />
                    <ArrowRight className="w-4 h-4 text-primary/50 -ml-1" />
                  </div>
                </div>
              )}

              <div className="group surface-card p-6 md:p-8 h-full">
                <div className="flex items-center justify-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center">
                    <step.icon className="w-6 h-6 text-primary-foreground" />
                  </div>
                  {/*
                    Composite-only number indicator: two stacked spans crossfade via opacity.
                    Idle: dim variant visible. Hover: bright + drop-shadow variant fades in.
                    Replaces `transition-all` (which animated non-composite `color`).
                  */}
                  <span className="relative font-mono text-h3 font-bold leading-none">
                    <span aria-hidden="true" className="block text-primary/30 transition-opacity duration-300 group-hover:opacity-0">
                      {step.step}
                    </span>
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 text-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100 drop-shadow-[0_0_12px_hsl(var(--primary)/0.45)]"
                    >
                      {step.step}
                    </span>
                    <span className="sr-only">{step.step}</span>
                  </span>
                </div>

                <h3 className="font-display text-h3 font-semibold mb-3 text-left">
                  {step.title}
                </h3>
                <p className="text-body text-muted-foreground mb-6 text-left">
                  {step.description}
                </p>

                <ul className="w-fit space-y-2 text-left">
                  {step.outputs.map((output) => (
                    <li
                      key={output}
                      className="flex items-center justify-start gap-2 text-sm text-muted-foreground"
                    >
                      <div className="w-1 h-1 rounded-full bg-primary" />
                      {output}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 md:mt-12">
          <Link
            href={methodHref}
            className="rotating-border inline-flex h-12 items-center justify-center gap-2 rounded-xl px-7 font-semibold text-foreground"
          >
            {isItalian ? "Scopri di più sul metodo" : "Learn more about the method"}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
      </section>
  );
}
